import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, fileUrl } from "../api";
import { useLang } from "../i18n";
import { Reveal, SectionHead } from "../components/Section";
import { PlaceholderImage } from "../components/PlaceholderImage";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

export default function Journal() {
    const { t } = useLang();
    const [articles, setArticles] = useState([]);

    useEffect(() => {
        api.get("/journal").then((r) => setArticles(r.data.articles || [])).catch(() => {});
    }, []);

    const [featured, ...rest] = articles;

    return (
        <div data-testid="journal-page" className="pt-32 pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHead eyebrow="Stories from the movement" title={t("journal_title")} testId="journal-title" />

                {featured && (
                    <Reveal delay={0.05}>
                        <Link to={`/journal/${featured.slug}`} data-testid={`article-featured-${featured.slug}`}
                            className="group mt-14 grid grid-cols-1 lg:grid-cols-2 rounded-3xl border border-lakou-gold/20 bg-lakou-coal/60 overflow-hidden hover:border-lakou-gold/45 transition-colors duration-500">
                            <div className="aspect-[16/10] lg:aspect-auto lg:min-h-[380px] overflow-hidden">
                                {featured.cover_path
                                    ? <img src={fileUrl(featured.cover_path)} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    : <PlaceholderImage letter={featured.title[0]} label={featured.category} className="w-full h-full" />}
                            </div>
                            <div className="p-8 sm:p-12 flex flex-col justify-center">
                                <div className="flex items-center gap-3 font-mono2 text-[10px] uppercase tracking-[0.25em]">
                                    <span className="text-lakou-gold">{featured.category}</span>
                                    <span className="text-lakou-sand">· Featured</span>
                                </div>
                                <div className="mt-4 font-display font-extrabold text-2xl sm:text-4xl text-lakou-cream leading-tight group-hover:text-lakou-goldbright transition-colors">{featured.title}</div>
                                <p className="mt-4 text-sm sm:text-base text-lakou-grey leading-relaxed line-clamp-4">{featured.excerpt}</p>
                                <div className="mt-8 flex items-center justify-between font-mono2 text-[10px] uppercase tracking-[0.2em] text-lakou-sand">
                                    <span>{featured.author}{featured.published_at ? ` · ${fmtDate(featured.published_at)}` : ""}</span>
                                    <span className="inline-flex items-center gap-1 text-lakou-gold">{t("read")} <ArrowRight size={11} /></span>
                                </div>
                            </div>
                        </Link>
                    </Reveal>
                )}

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((a, i) => (
                        <Reveal key={a.id} delay={(i % 3) * 0.08}>
                            <Link to={`/journal/${a.slug}`} data-testid={`article-card-${a.slug}`}
                                className="group block rounded-3xl border border-lakou-gold/15 bg-lakou-coal/60 overflow-hidden hover:border-lakou-gold/40 transition-colors duration-500 h-full">
                                <div className="aspect-[16/10] overflow-hidden">
                                    {a.cover_path
                                        ? <img src={fileUrl(a.cover_path)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        : <PlaceholderImage letter={a.title[0]} label={a.category} className="w-full h-full" />}
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em]">
                                        <span className="text-lakou-gold">{a.category}</span>
                                        {a.published_at && <span className="text-lakou-sand">· {fmtDate(a.published_at)}</span>}
                                    </div>
                                    <div className="mt-2 font-display font-bold text-lg text-lakou-cream leading-snug group-hover:text-lakou-goldbright transition-colors">{a.title}</div>
                                    <p className="mt-3 text-sm text-lakou-grey leading-relaxed line-clamp-3">{a.excerpt}</p>
                                    <div className="mt-4 flex items-center justify-between font-mono2 text-[10px] uppercase tracking-[0.2em] text-lakou-sand">
                                        <span>{a.author}</span>
                                        <span className="inline-flex items-center gap-1 text-lakou-gold">{t("read")} <ArrowRight size={11} /></span>
                                    </div>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                    {articles.length === 0 && <p className="text-lakou-sand text-sm col-span-full" data-testid="journal-empty">{t("journal_empty")}</p>}
                </div>
            </div>
        </div>
    );
}
